import { useEffect } from 'react';

import hhproScreenShown from '@hh.ru/analytics-js-events/build/xhh/applicant/hhpro/hhpro_promo_screen_shown';
import { translation, TranslatedComponent } from '@hh.ru/front-static-app';
import { GridColumn, GridRow, Title, VSpacing, GridLayout, useBreakpoint, TitleProps } from '@hh.ru/magritte-ui';
import { useSelectorNonNullable } from '@hh.ru/redux-create-reducer';

import PageLayout from 'src/app/layouts/PageLayout';
import HhProLayout from 'src/app/layouts/PageLayout/Layouts/HhProLayout';
import PaymentState from 'src/components/HhPro/PaymentState';
import { SubscriptionAction, usePayment } from 'src/hooks/hhPro/usePayment';
import useSubscribeAction from 'src/hooks/hhPro/useSubscribeAction';
import useActiveSubscription from 'src/hooks/useActiveSubscription';

import About from 'src/pages/HhProPromo/components/About';
import Disclaimer from 'src/pages/HhProPromo/components/Disclaimer';
import Faq from 'src/pages/HhProPromo/components/Faq';
import Header from 'src/pages/HhProPromo/components/Header';
import Service from 'src/pages/HhProPromo/components/Service';
import SubscribeControl from 'src/pages/HhProPromo/components/SubscribeControl';
import Expire from 'src/pages/HhProPromo/components/SubscribeControl/Expire';
import Oferta from 'src/pages/HhProPromo/components/SubscribeControl/Oferta';

import styles from './styles.less';

const TrlKeys = {
    seoTitle: 'hhpro.promo.seo.title',
    innerSubTitle: 'hhpro.promo.innerSubTitle',
    faqSubTitle: 'hhpro.promo.faqSubTitle',
};

const HhProPromo: TranslatedComponent = ({ trls }) => {
    const hhpro = useSelectorNonNullable((store) => store.hhPro);
    const breakpoint = useBreakpoint();
    const activeSubscription = useActiveSubscription();
    const action = useSubscribeAction(activeSubscription);

    const paymentParams = usePayment(hhpro.id, activeSubscription);

    let titleSize: TitleProps['size'] = 'medium';
    if (breakpoint.isM) {
        titleSize = 'large';
    } else if (breakpoint.isGtM) {
        titleSize = 'extra-large';
    }

    useEffect(() => {
        hhproScreenShown();
    }, []);

    return (
        <PageLayout layout={HhProLayout} title={trls[TrlKeys.seoTitle]}>
            <Header {...paymentParams} />
            <GridLayout>
                <GridRow>
                    <GridColumn xs={4} s={8} m={12}>
                        <VSpacing default={32} gteM={64} />
                        <Title Element="h2" size={titleSize} alignment="center" style="contrast">
                            {trls[TrlKeys.innerSubTitle]}
                        </Title>
                        <VSpacing default={20} gteM={36} />
                        <About tariffId={hhpro.id} />
                        <VSpacing default={40} gteM={80} />
                        {hhpro.serviceDetails.map((service) => (
                            <Service key={service.id} service={service} tariffId={hhpro.id} />
                        ))}
                    </GridColumn>
                </GridRow>
                <GridRow>
                    <GridColumn xs={4} s={8} m={12}>
                        {(breakpoint.isGtS || action === SubscriptionAction.Cancel) && (
                            <>
                                <div className={styles.payment}>
                                    {[SubscriptionAction.Payment, SubscriptionAction.Renew].includes(action) && (
                                        <Oferta />
                                    )}
                                    {action === SubscriptionAction.Cancel && (
                                        <Expire expiredAt={activeSubscription?.expiredAt} />
                                    )}
                                    <VSpacing default={12} gteM={24} />
                                    <SubscribeControl
                                        currency={hhpro.currency}
                                        price={hhpro.price}
                                        tariffId={hhpro.id}
                                        subscription={activeSubscription}
                                        label="desktop-footer"
                                        {...paymentParams}
                                    />
                                    {action === SubscriptionAction.Renew && (
                                        <>
                                            <VSpacing default={12} gteM={24} />
                                            <Expire expiredAt={activeSubscription?.expiredAt} canceled />
                                        </>
                                    )}
                                </div>
                                <VSpacing default={40} gteM={80} />
                            </>
                        )}
                        {breakpoint.isMobile && action === SubscriptionAction.Renew && (
                            <div className={styles.payment}>
                                <Expire expiredAt={activeSubscription?.expiredAt} canceled />
                                <VSpacing default={40} gteM={80} />
                            </div>
                        )}
                    </GridColumn>
                </GridRow>
                <GridRow>
                    <GridColumn xs={0} m={1} />
                    <GridColumn xs={4} s={8} m={10}>
                        <Title Element="h2" size={titleSize} alignment="center" style="contrast">
                            {trls[TrlKeys.faqSubTitle]}
                        </Title>
                        <VSpacing default={20} gteM={36} />
                        <Faq />
                        <VSpacing default={64} />
                    </GridColumn>
                </GridRow>
                <GridRow>
                    <GridColumn xs={0} m={1} l={0} />
                    <GridColumn xs={4} s={8} m={10} l={12}>
                        <Disclaimer />
                    </GridColumn>
                </GridRow>
                <VSpacing default={40} gteM={80} />
            </GridLayout>
            {[SubscriptionAction.Payment, SubscriptionAction.Renew].includes(action) && (
                <div className={styles.paymentMobile}>
                    <SubscribeControl
                        currency={hhpro.currency}
                        price={hhpro.price}
                        tariffId={hhpro.id}
                        stretched
                        subscription={activeSubscription}
                        label="mobile-sticky"
                        {...paymentParams}
                    />
                    <VSpacing default={12} gteM={24} />
                    <Oferta />
                </div>
            )}
            <PaymentState
                {...paymentParams}
                price={hhpro.price}
                currency={hhpro.currency}
                subscription={activeSubscription}
            />
        </PageLayout>
    );
};

export default translation(HhProPromo);
